import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { FocusVisibleManager } from "use-focus-visible";
import hash from "object-hash";
import { Toggle } from "../../form";
import { Icon } from "../../icon";
import { Column } from "./column";
import { StyledTable } from "./styledtable.styles";
import { StyledRow } from "../row/styledrow.styles";
import { HeaderCell } from "../cell/headercell";
import { convertChildrenToColumns, getColumnsKey, getSorters } from "../util";
import { FooterComponents } from "../footer";
import { StyledBody } from "./styledbody.styles";
import { StyledToggle } from "../cell/styledtoggle";
import { Row } from "../row/row";
const TableHeader = "thead";
function Table({ type = "TB01", dataSource, columns, tableLayout = "auto", children, summary, width = "auto", onSelect, primaryKey, defaultCheckedRowsPrimaryKeys = [], onRowClick, showHeader = true, }) {
    const [values, setValues] = useState({
        checkbox: defaultCheckedRowsPrimaryKeys,
    });
    let baseColumns = columns || convertChildrenToColumns(children);
    const [dataIndex, setDataIndex] = useState(String(baseColumns[type === "TB01" || type === "TB02" ? 0 : 1].dataIndex));
    const sorters = useMemo(() => getSorters(baseColumns), []);
    const [dir, setDir] = useState("descend");
    const getSortedData = useMemo(() => {
        let results = [...dataSource];
        if (!dataIndex)
            return results;
        const { sorterFn } = sorters[dataIndex];
        if (sorterFn && !!dir) {
            results = results.sort(sorterFn);
        }
        if (dir === "ascend") {
            results.reverse();
        }
        if (values.checkbox.length - 1 !== results.length) {
            const neValues = values.checkbox.filter((r) => results.findIndex((e) => e[primaryKey] === r) >= 0);
            if (neValues.length > 0) {
                neValues.unshift("toggleAll");
            }
            setValues({ checkbox: neValues });
        }
        return results;
    }, [dataIndex, dir, dataSource.length]);
    function disabledOrReadOnlyPredicat(e) {
        return !e.readonly || !e.disabled;
    }
    const onChange = (value, name) => {
        if (value === "toggleAll") {
            const changedValues = { checkbox: [] };
            if (values.checkbox.length - 1 !==
                getSortedData.filter(disabledOrReadOnlyPredicat).length) {
                changedValues.checkbox = [
                    "toggleAll",
                    ...getSortedData
                        .filter(disabledOrReadOnlyPredicat)
                        .map((e) => e[primaryKey]),
                ];
            }
            setValues(changedValues);
            // done fire if only the 'toggleAll' checkbox is selected/checked
            const isAtLeastOneRowNotReadonlyOrDisabled = changedValues.checkbox.length > 1;
            if (onSelect && isAtLeastOneRowNotReadonlyOrDisabled) {
                onSelect(changedValues.checkbox);
            }
            return;
        }
        let changedValues = { checkbox: [] };
        if (values[name].includes(value)) {
            const nextValues = values[name].filter((itemVal) => itemVal !== value);
            changedValues = Object.assign(Object.assign({}, values), { [name]: nextValues.length === 1 ? [] : nextValues });
        }
        else {
            const nextValues = [...values[name], value];
            changedValues = Object.assign(Object.assign({}, values), { [name]: nextValues.length === 1 ? ["toggleAll", ...nextValues] : nextValues });
        }
        setValues(changedValues);
        if (onSelect) {
            onSelect(changedValues.checkbox);
        }
    };
    function injectCheckBoxes() {
        baseColumns = [
            {
                dataIndex: "checkbox",
                key: "checkbox",
                width: "40",
                sortDirections: null,
                render: (_, record, index) => {
                    if (typeof index === "undefined") {
                        return (_jsx(StyledToggle, { alignment: "horizontal", label: "", isFlyout: true, onChange: (e) => onChange(e, `checkbox`), type: "checkbox", name: "checkbox", symbol: values.checkbox.length >
                                getSortedData.filter(disabledOrReadOnlyPredicat).length
                                ? "Checkmark"
                                : "Minus", items: [{ id: `checkbox`, value: `toggleAll`, text: "" }], values: values }, "toggle-all"));
                    }
                    if (!record || record.disabled || record.readonly) {
                        return null;
                    }
                    if (index >= 0) {
                        return (_jsx("div", { children: _jsx(Toggle, { tabIndex: -1, alignment: "horizontal", label: "", onChange: (e) => onChange(e, `checkbox`), type: "checkbox", name: "checkbox", isFlyout: true, items: [
                                    {
                                        id: `checkbox`,
                                        value: String(record[primaryKey]),
                                        text: "",
                                    },
                                ], values: values }, primaryKey ? String(record[primaryKey]) : String(index)) }));
                    }
                    return null;
                },
            },
            ...baseColumns,
        ];
    }
    function injectRightArrows() {
        baseColumns = [
            ...baseColumns,
            {
                dataIndex: "checkbox",
                key: "checkbox",
                width: "40",
                sortDirections: null,
                render: (_, record) => !record.disabled && !record.readonly ? (_jsx(Icon, { withHitSpace: true, symbol: "ChevronRight" })) : null,
            },
        ];
    }
    if (type === "TB02") {
        injectCheckBoxes();
    }
    if (type === "TB04") {
        injectRightArrows();
    }
    const columnsKey = getColumnsKey(baseColumns);
    const onCellClick = (dIndex) => {
        const { directionGenerator } = sorters[String(dIndex)];
        let t = directionGenerator.next().value;
        if (t === null && dIndex !== dataIndex) {
            t = directionGenerator.next().value;
        }
        setDataIndex(String(dIndex));
        setDir(t);
    };
    function renderHeaderRow() {
        return (_jsx(StyledRow, Object.assign({ rowType: "header", tableType: type }, { children: baseColumns.map((column, colIndex) => {
                const { render, dataIndex: dIndex, title, width: columnWidth, align, } = column;
                const key = columnsKey[colIndex];
                let additionalCellProps;
                if (column.onHeaderCell) {
                    additionalCellProps = column.onHeaderCell(column);
                }
                if (column.sorter) {
                    additionalCellProps = Object.assign(Object.assign({}, additionalCellProps), { onClick: () => onCellClick(String(dIndex)) });
                }
                return (_jsx(FocusVisibleManager, { children: _jsx(HeaderCell, Object.assign({ align: align, width: columnWidth, additionalProps: additionalCellProps, dataIndex: dataIndex, render: type === "TB04" ? null : render, sortIcon: !!column.sorter, tableType: type, disabled: column.disabled, readonly: column.readonly, sortOrder: dIndex === dataIndex ? dir : null }, { children: title }), key) }, hash(column)));
            }) })));
    }
    function renderTableRows() {
        return getSortedData.map((record, index) => {
            return (_jsx(Row, { onClick: () => {
                    if (type === "TB04" && onRowClick) {
                        onRowClick(record);
                    }
                }, checked: values.checkbox.some((v) => v === String(record[primaryKey])), onChange: (value, name) => {
                    if (onChange) {
                        onChange(value, name);
                    }
                }, type: type, primaryKey: primaryKey, record: record, baseColumns: baseColumns, columnsKey: columnsKey, index: index, odd: type === "TB01" && (index + 1) % 2 === 0 }, primaryKey ? String(record[primaryKey]) : String(index)));
        });
    }
    return (_jsxs(StyledTable, Object.assign({ tableType: type, width: width, tableLayout: tableLayout }, { children: [showHeader && _jsx(TableHeader, { children: renderHeaderRow() }), _jsx(StyledBody, { children: renderTableRows() }), summary && _jsx("tfoot", { children: summary(getSortedData) })] })));
}
Table.Column = Column;
Table.Summary = FooterComponents;
export { Table };
